<script>
import CustomButton from 'shared/components/Button.vue';
import Spinner from 'shared/components/Spinner.vue';
import { getRegexp } from 'shared/helpers/Validators';
import { getContrastingTextColor } from '@chatwoot/utils';
import PhoneInput from './PhoneInput.vue';
import ChatIcon from './ChatIcon.vue';
import { FormKit, createInput } from '@formkit/vue';
import { generateRedirectUrl } from '../api/pre_chat';
import MessageFormatter from 'shared/helpers/MessageFormatter';

export default {
  components: {
    FormKit,
    CustomButton,
    Spinner,
    ChatIcon,
  },
  name: 'PreChatForm',
  setup() {
    const phoneInput = createInput(PhoneInput, {
      props: ['hasErrorInPhoneInput'],
    });

    return { phoneInput };
  },
  data() {
    // Cross-domain attribution params forwarded from voltade.com's WhatsApp CTA.
    // Absent for direct/organic visitors — null is sent in that case.
    const searchParams = new URLSearchParams(window.location.search);
    return {
      formValues: {},
      businessName: window.preChatConfigs.businessName,
      businessAvatarUrl: window.preChatConfigs.businessAvatarUrl,
      businessAgreement: window.preChatConfigs.businessAgreement,
      channelType: window.preChatConfigs.channelType,
      websiteToken: window.preChatConfigs.websiteToken,
      visitorId: window.preChatConfigs.visitorId,
      preChatFormOptions: window.preChatConfigs.preChatFormOptions || {},
      vsid: searchParams.get('vsid'),
      vvid: searchParams.get('vvid'),
      vsrc: searchParams.get('vsrc'),
      isLoading: false,
      hasErrorInPhoneInput: false,
    };
  },
  computed: {
    isPhoneNumberRequired() {
      return this.channelType === 'whatsapp';
    },
    submitButtonColor() {
      const BUTTON_COLOR_MAP = {
        whatsapp: '#25D366',
        email: '#2781F6',
      };
      return BUTTON_COLOR_MAP[this.channelType];
    },
    textColor() {
      return getContrastingTextColor(this.submitButtonColor);
    },
    consentText() {
      let text = '';
      if (this.businessAgreement && this.businessAgreement.trim().length) {
        // Use custom business agreement if provided
        text = this.businessAgreement
          .replace(/\{\{\s*business_name\s*\}\}/gi, `**${this.businessName}**`)
          .replace(/\{\{\s*businessName\s*\}\}/gi, `**${this.businessName}**`);
      } else {
        // Default consent text
        text = `By continuing, I agree that **${this.businessName}** may collect, use and disclose my personal data in accordance with **${this.businessName}**'s privacy policy.`;
      }
      const messageFormatter = new MessageFormatter(text);
      return messageFormatter.formattedMessage;
    },
    preChatFields() {
      const defaultFields = [
        {
          name: 'name',
          type: 'text',
          label: 'Name',
          placeholder: 'Enter your name',
          required: true,
          regex_pattern: null,
          enabled: true,
        },
        ...(this.channelType === 'whatsapp'
          ? [
              {
                name: 'phoneNumber',
                type: 'text',
                label: 'Phone Number',
                placeholder: 'Enter your phone number',
                required: true,
                regex_pattern: null,
                enabled: true,
              },
            ]
          : []),
        {
          name: 'emailAddress',
          type: 'text',
          label: 'Email',
          placeholder: 'Enter your email',
          required: this.channelType === 'email',
          regex_pattern: null,
          enabled: true,
        },
      ];

      if (this.preChatFormOptions.pre_chat_fields) {
        const additionalFields = this.preChatFormOptions.pre_chat_fields.filter(
          field => !['name', 'phoneNumber', 'emailAddress'].includes(field.name)
        );
        return [...defaultFields, ...additionalFields];
      }

      return defaultFields;
    },
    enabledPreChatFields() {
      return this.preChatFields
        .filter(field => field.enabled)
        .map(field => {
          let type = field.type;
          if (field.name === 'phoneNumber') {
            type = this.phoneInput;
          } else if (type === 'link') {
            type = 'url';
          } else if (type === 'list') {
            type = 'select';
          }
          return {
            ...field,
            type,
          };
        });
    },
    isConsentAccepted() {
      return Boolean(this.formValues.privacyConsent);
    },
  },
  methods: {
    labelClass(input) {
      const { state, node, type } = input.context;
      const hasErrors = state.invalid;
      const fieldName = node && node.name ? node.name : undefined;
      if (type === 'checkbox' || fieldName === 'privacyConsent') {
        return !hasErrors
          ? 'text-[10px] leading-4 text-n-slate-10 !font-normal normal-case flex-1 min-w-0'
          : 'text-[10px] leading-4 text-n-ruby-10 !font-normal normal-case flex-1 min-w-0';
      }
      return !hasErrors
        ? 'text-sm font-medium text-n-slate-12'
        : 'text-sm font-medium text-n-ruby-10';
    },
    wrapperClass(input) {
      const { node, type } = input.context;
      const fieldName = node && node.name ? node.name : undefined;
      if (type === 'checkbox' || fieldName === 'privacyConsent') {
        return 'mt-2 rounded-md border border-solid border-n-slate-6 bg-n-slate-3 px-1 py-2 text-left flex items-start gap-2';
      }
      return '';
    },
    inputClass(input) {
      const { state, family: classification, type } = input.context;
      const hasErrors = state.invalid;
      if (classification === 'box' && type === 'checkbox') {
        return 'mt-0.5 ml-1';
      }
      if (type === 'phoneInput') {
        this.hasErrorInPhoneInput = hasErrors;
      }
      if (!hasErrors) {
        return `mt-1 rounded w-full py-2 px-3`;
      }
      return `mt-1 rounded w-full py-2 px-3 error`;
    },
    isContactFieldRequired(field) {
      return this.preChatFields.find(option => option.name === field).required;
    },
    getLabel({ label }) {
      return label;
    },
    getPlaceHolder({ placeholder }) {
      return placeholder;
    },
    getValue({ name, type }) {
      if (type === 'select') {
        return this.enabledPreChatFields.find(option => option.name === name)
          .values[this.formValues[name]];
      }
      return this.formValues[name] || null;
    },
    getOptions(item) {
      if (item.type === 'select') {
        let values = {};
        item.values.forEach((value, index) => {
          values = {
            ...values,
            [index]: value,
          };
        });
        return values;
      }
      return {};
    },
    getValidation({ type, name, field_type, regex_pattern }) {
      let regex = regex_pattern ? getRegexp(regex_pattern) : null;
      const validations = {
        emailAddress: 'email',
        phoneNumber: ['startsWithPlus', 'isValidPhoneNumber'],
        url: 'url',
        date: 'date',
        text: null,
        select: null,
        number: null,
        checkbox: false,
        contact_attribute: regex ? [['matches', regex]] : null,
        conversation_attribute: regex ? [['matches', regex]] : null,
        opportunity_attribute: regex ? [['matches', regex]] : null,
      };

      const validationKeys = Object.keys(validations);
      const isRequired = this.isContactFieldRequired(name);
      const validation = isRequired ? ['required'] : ['optional'];

      if (
        validationKeys.includes(name) ||
        validationKeys.includes(type) ||
        validationKeys.includes(field_type)
      ) {
        const validationType =
          validations[type] || validations[name] || validations[field_type];
        const allValidations = validationType
          ? validation.concat(validationType)
          : validation;
        return allValidations.join('|');
      }

      return '';
    },
    async onSubmit() {
      if (!this.formValues.privacyConsent) {
        return;
      }
      const { emailAddress, phoneNumber, name, ...rest } = this.formValues;
      
      const customAttributes = {};
      const opportunityCustomAttributes = {};
      this.enabledPreChatFields.forEach(field => {
        if (field.field_type === 'contact_attribute' || field.field_type === 'conversation_attribute') {
           if (rest[field.name]) {
             if (field.type === 'select') {
               customAttributes[field.name] = field.values[rest[field.name]];
             } else {
               customAttributes[field.name] = rest[field.name];
             }
           }
        } else if (field.field_type === 'opportunity_attribute') {
           if (rest[field.name]) {
             if (field.type === 'select') {
               opportunityCustomAttributes[field.name] = field.values[rest[field.name]];
             } else {
               opportunityCustomAttributes[field.name] = rest[field.name];
             }
           }
        }
      });

      this.isLoading = true;
      await generateRedirectUrl({
        websiteToken: this.websiteToken,
        channelType: this.channelType,
        visitorId: this.visitorId,
        name,
        phoneNumber,
        emailAddress,
        customAttributes,
        opportunityCustomAttributes,
        vsid: this.vsid,
        vvid: this.vvid,
        vsrc: this.vsrc,
      })
        .then(result => {
          const { redirect_url } = result.data;
          window.location.href = redirect_url;
        })
        .catch(error => {
          console.error(error);
        })
        .finally(() => {
          setTimeout(() => {
            this.isLoading = false;
          }, 1000);
        });
    },
  },
};
</script>

<template>
  <FormKit
    v-model="formValues"
    type="form"
    form-class="flex flex-col flex-1 w-full p-6 overflow-y-auto"
    :incomplete-message="false"
    :submit-attrs="{
      inputClass: 'hidden',
      wrapperClass: 'hidden',
    }"
    @submit="onSubmit"
  >
    <div class="flex flex-col items-center mb-4 gap-2">
      <img
        v-if="businessAvatarUrl"
        :src="businessAvatarUrl"
        alt="Business Avatar"
        class="w-20 h-20 rounded-full mx-auto"
      />
      <h1 class="text-xl font-semibold">{{ businessName }}</h1>
    </div>
    <FormKit
      v-for="item in enabledPreChatFields"
      :key="item.name"
      :name="item.name"
      :type="item.type"
      :label="getLabel(item)"
      :placeholder="getPlaceHolder(item)"
      :validation="getValidation(item)"
      v-bind="
        item.type === 'select'
          ? {
              options: getOptions(item),
            }
          : undefined
      "
      :label-class="context => labelClass(context)"
      :input-class="context => inputClass(context)"
      :wrapper-class="context => wrapperClass(context)"
      :validation-messages="{
        startsWithPlus: $t(
          'PRE_CHAT_FORM.FIELDS.PHONE_NUMBER.DIAL_CODE_VALID_ERROR'
        ),
        isValidPhoneNumber: $t('PRE_CHAT_FORM.FIELDS.PHONE_NUMBER.VALID_ERROR'),
        email: $t('PRE_CHAT_FORM.FIELDS.EMAIL_ADDRESS.VALID_ERROR'),
        required: $t('PRE_CHAT_FORM.REQUIRED'),
        matches: item.regex_cue
          ? item.regex_cue
          : $t('PRE_CHAT_FORM.REGEX_ERROR'),
      }"
      :has-error-in-phone-input="hasErrorInPhoneInput"
    />

    <!-- Consent checkbox rendered separately to allow rich label formatting -->
    <FormKit
      name="privacyConsent"
      type="checkbox"
      :validation="'required'"
      :label-class="context => labelClass(context)"
      :input-class="context => inputClass(context)"
      :wrapper-class="context => wrapperClass(context)"
    >
      <template #label>
        <div
          class="text-[10px] leading-4 text-n-slate-10 !font-normal normal-case break-all whitespace-pre-wrap"
          v-html="consentText"
        />
      </template>
    </FormKit>

    <CustomButton
      type="submit"
      class="mt-4 font-medium flex items-center justify-center gap-2"
      block
      :bg-color="submitButtonColor"
      :text-color="textColor"
      :disabled="isLoading || !isConsentAccepted"
    >
      <Spinner v-if="isLoading" class="p-0" />
      <ChatIcon :channel-type="channelType" v-else />
      Continue to chat
    </CustomButton>

    <!-- Powered by Voltade Footer -->
    <div
      class="text-[11px] text-n-slate-8 text-center mt-2 flex items-center justify-center"
    >
      <img
        src="~dashboard/assets/images/brand-assets/logo_thumbnail.svg"
        alt="Voltade"
        class="w-4 h-4 mr-1"
      />
      <a
        href="https://voltade.com"
        target="_blank"
        class="underline underline-offset-2 cursor-pointer transition-colors hover:text-n-slate-10"
      >
        {{ $t('POWERED_BY') }}
      </a>
    </div>
  </FormKit>
</template>

<style lang="scss">
.formkit-outer {
  @apply mt-2;

  .formkit-inner {
    input.error,
    textarea.error,
    select.error {
      @apply outline-n-ruby-8 dark:outline-n-ruby-8 hover:outline-n-ruby-9 dark:hover:outline-n-ruby-9 focus:outline-n-ruby-9 dark:focus:outline-n-ruby-9;
    }

    input[type='checkbox'] {
      @apply size-3 outline-none;
    }
  }
}

[data-invalid] .formkit-message {
  @apply text-n-ruby-10 block text-xs font-normal my-0.5 w-full;
}

.formkit-outer[data-type='checkbox'] .formkit-wrapper {
  @apply flex items-start gap-2;
}

.link {
  @apply text-woot-500 underline hover:text-woot-700;
}

.formkit-messages {
  @apply list-none m-0 p-0;
}
</style>
